import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import type { MarketingInspirationItem, MarketingInspirationItemType } from './types';
import { buildMarketingInspirationMismatchWarning, sortMarketingInspirationItems } from './inspiration';

const { Paragraph, Text } = Typography;

interface MarketingInspirationPickerProps {
  items: MarketingInspirationItem[];
  loading?: boolean;
  selectedIds: string[];
  onChange: (nextIds: string[]) => void;
  selectedCharacterId?: string;
  selectedCharacterName?: string;
  helperText?: string;
  embedded?: boolean;
}

const TYPE_COLOR: Record<MarketingInspirationItemType, string> = {
  scene: 'blue',
  quote: 'gold',
  vibe: 'purple',
};

const TYPE_LABEL: Record<MarketingInspirationItemType, string> = {
  scene: 'Scene',
  quote: 'Quote',
  vibe: 'Vibe',
};

const MarketingInspirationPicker: React.FC<MarketingInspirationPickerProps> = ({
  items,
  loading = false,
  selectedIds,
  onChange,
  selectedCharacterId,
  selectedCharacterName,
  helperText = 'Choose approved scenes, quotes, and vibes to anchor the prompt.',
  embedded = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | MarketingInspirationItemType>('all');
  const [showDrafts, setShowDrafts] = useState(false);
  const selectedItems = useMemo(
    () =>
      sortMarketingInspirationItems(
        selectedIds
          .map(id => items.find(item => item.id === id) || null)
          .filter((item): item is MarketingInspirationItem => Boolean(item))
      ),
    [items, selectedIds]
  );
  const visibleItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return sortMarketingInspirationItems(items).filter(item => {
      const normalizedStatus = String(item.status || 'draft').toLowerCase();
      if (normalizedStatus === 'skipped') return false;
      if (!showDrafts && normalizedStatus !== 'approved') return false;
      if (typeFilter !== 'all' && item.type !== typeFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      const haystack = [
        item.title,
        item.text,
        item.quoteText,
        ...(item.timePeriods || []),
        item.sceneVideo?.sceneMode,
        item.sceneVideo?.prompt,
        ...(item.sceneVideo?.beats || []),
        ...(item.sceneVideo?.dialogueOverlayLines || []),
        ...(item.characterNames || []),
        ...(item.vibeTags || []),
        ...(item.contentFlags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [items, search, showDrafts, typeFilter]);
  const warningMessage = useMemo(
    () =>
      buildMarketingInspirationMismatchWarning({
        selectedCharacterId,
        selectedCharacterName,
        items: selectedItems,
      }),
    [selectedCharacterId, selectedCharacterName, selectedItems]
  );

  const toggleItem = (itemId: string) => {
    if (selectedIds.includes(itemId)) {
      onChange(selectedIds.filter(id => id !== itemId));
      return;
    }
    onChange([...selectedIds, itemId]);
  };

  const content = (
    <>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <BulbOutlined />
            <Text strong>Inspiration</Text>
          </Space>
          <Space size={8}>
            <Button size="small" onClick={() => setModalOpen(true)}>
              Choose Inspiration
            </Button>
            {selectedIds.length ? (
              <Button size="small" onClick={() => onChange([])}>
                Clear
              </Button>
            ) : null}
          </Space>
        </Space>
        <Text type="secondary">{helperText}</Text>
        {warningMessage ? <Alert type="warning" showIcon message={warningMessage} /> : null}
        {selectedItems.length ? (
          <Space wrap size={8}>
            {selectedItems.map(item => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  borderRadius: 10,
                  border: '1px solid var(--border-subtle)',
                  background: 'var(--surface-raised)',
                  maxWidth: '100%',
                }}
              >
                <Tag color={TYPE_COLOR[item.type]} style={{ marginInlineEnd: 0 }}>
                  {TYPE_LABEL[item.type]}
                </Tag>
                <Space direction="vertical" size={0} style={{ minWidth: 0 }}>
                  <Text strong ellipsis style={{ maxWidth: 280 }}>
                    {item.title || item.quoteText || 'Untitled inspiration'}
                  </Text>
                  <Text type="secondary" ellipsis style={{ maxWidth: 320, fontSize: 12 }}>
                    {item.quoteText || item.text}
                  </Text>
                </Space>
                <Button size="small" onClick={() => toggleItem(item.id)}>
                  Remove
                </Button>
              </div>
            ))}
          </Space>
        ) : (
          <Text type="secondary">No inspiration selected.</Text>
        )}
      </Space>

      <Modal
        title="Choose Inspiration"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={920}
        destroyOnClose={false}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
            <Input.Search
              placeholder="Search scenes, quotes, characters, tags..."
              allowClear
              value={search}
              onChange={event => setSearch(event.target.value)}
              style={{ width: 320 }}
            />
            <Space wrap>
              <Button onClick={() => setShowDrafts(value => !value)}>
                {showDrafts ? 'Approved only' : 'Show drafts'}
              </Button>
              <Select
                value={typeFilter}
                onChange={value => setTypeFilter(value)}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'scene', label: 'Scenes' },
                  { value: 'quote', label: 'Quotes' },
                  { value: 'vibe', label: 'Moods' },
                ]}
                style={{ width: 160 }}
              />
            </Space>
          </Space>
          {visibleItems.length ? (
            <Space direction="vertical" size={8} style={{ width: '100%', maxHeight: '62vh', overflowY: 'auto' }}>
              {visibleItems.map(item => {
                const isSelected = selectedIds.includes(item.id);
                const excerpt = item.quoteText || item.text;
                return (
                  <Card
                    key={item.id}
                    size="small"
                    style={{
                      borderColor: isSelected ? 'var(--accent-primary, #1677ff)' : undefined,
                      boxShadow: isSelected ? '0 0 0 1px rgba(22, 119, 255, 0.15)' : undefined,
                    }}
                  >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space wrap size={6}>
                          <Tag color={TYPE_COLOR[item.type]}>{TYPE_LABEL[item.type]}</Tag>
                          <Tag color={item.status === 'approved' ? 'green' : 'default'}>
                            {item.status === 'approved' ? 'Approved' : 'Draft'}
                          </Tag>
                          {item.origin ? <Tag>{item.origin}</Tag> : null}
                          {item.recommended ? <Tag color="green">Recommended</Tag> : null}
                          {item.sourceVerified ? <Tag color="cyan">Verified passage</Tag> : null}
                          {item.spoilerLevel ? <Tag color="orange">Spoilers: {item.spoilerLevel}</Tag> : null}
                        </Space>
                        <Button type={isSelected ? 'default' : 'primary'} onClick={() => toggleItem(item.id)}>
                          {isSelected ? 'Remove' : 'Use This'}
                        </Button>
                      </Space>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text strong>{item.title || 'Untitled inspiration'}</Text>
                        <Paragraph style={{ marginBottom: 0 }}>
                          {item.sourceExcerpt || excerpt || 'No content available.'}
                        </Paragraph>
                        {item.authorSynopsis && item.editionKind === 'translation' ? (
                          <Text type="secondary">Author-language summary: {item.authorSynopsis}</Text>
                        ) : null}
                      </Space>
                      <Space wrap size={[6, 6]}>
                        {(item.timePeriods || []).map(period => (
                          <Tag key={`${item.id}-period-${period}`} color="cyan">
                            {period}
                          </Tag>
                        ))}
                        {(item.characterNames || []).map(name => (
                          <Tag key={`${item.id}-character-${name}`}>{name}</Tag>
                        ))}
                        {(item.vibeTags || []).map(tag => (
                          <Tag key={`${item.id}-vibe-${tag}`} color="purple">
                            {tag}
                          </Tag>
                        ))}
                        {(item.contentFlags || []).map(flag => (
                          <Tag key={`${item.id}-flag-${flag}`} color="red">
                            {flag}
                          </Tag>
                        ))}
                      </Space>
                      {item.usageHints?.videoStudio || item.usageHints?.characterStudio ? (
                        <Text type="secondary">
                          {item.usageHints.characterStudio || item.usageHints.videoStudio}
                        </Text>
                      ) : null}
                      {item.type === 'scene' && item.sceneVideo?.prompt ? (
                        <Text type="secondary">
                          {item.sceneVideo.prompt}
                        </Text>
                      ) : null}
                    </Space>
                  </Card>
                );
              })}
            </Space>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={loading ? 'Loading inspiration...' : 'No inspiration items match the current filters.'}
            />
          )}
        </Space>
      </Modal>
    </>
  );

  return embedded ? content : <Card size="small">{content}</Card>;
};

export default MarketingInspirationPicker;
